import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { LIEN_BOUTON } from "./coloriage-commun";
import { ALERTE_AMBRE, ALERTE_ROUGE, MessageDeRetour, type Retour } from "./coloriage-demandes";
import {
  type BilanCloture,
  demandeActive,
  messageDEchec,
  useApercuCloture,
  useClore,
  useNouvelleDemande,
} from "./coloriage-requetes";
import {
  bilanDeCloture,
  effacementEnClair,
  etatDePage,
  preChoixEnClair,
  resumeDemande,
} from "./coloriage-textes";
import type { CahierResume, ClotureApercu, Demande, NouvelleDemande, PageResume } from "./contract";
import { compter } from "./format";

/**
 * Ce que la grille d'un cahier lance sur le PC (ticket 3, #152) : les cases des pages, la
 * barre d'actions et la confirmation de la cloture. Textes et disposition :
 * `ColoriageCahier.dc.html` (cases, barre) et `ColoriageEtats.dc.html` (« Clore un cahier »).
 *
 * A part de `coloriage-cahier.tsx`, qui ne fait que les poser sur la grille.
 */

/**
 * Les pages cochees d'office : toutes jugees, aucune version bonne, pas de choix pose (celles
 * que la grille dit « aucune version bonne »), et pas deja dans une relance qui attend ou
 * tourne : la cocher de nouveau ferait rendre la page deux fois.
 */
export function pagesCocheesDOffice(
  pages: readonly PageResume[],
  demandes: readonly Demande[],
): number[] {
  const relancees = new Set(
    demandes
      .filter((demande) => demande.type === "rendre" && demandeActive(demande))
      .flatMap((demande) => demande.pages.map((page) => page.id)),
  );
  return pages
    .filter((page) => etatDePage(page).ton === "sans_bonne" && !relancees.has(page.id))
    .map((page) => page.id);
}

/**
 * Les cases de la grille. Tant que Nicolas n'a touche a aucune case, elles suivent les pages
 * cochees d'office, meme quand la grille se relit ; sa premiere case fige la selection. Une
 * selection ne vaut que pour son cahier, et un cahier clos n'en a aucune.
 */
export function useCasesDuCahier(
  cahier: Pick<CahierResume, "id" | "etat">,
  pages: readonly PageResume[],
  demandes: readonly Demande[],
): {
  readonly cochees: readonly number[];
  readonly basculer: (pageId: number) => void;
  readonly vider: () => void;
} {
  const [selection, setSelection] = useState<{
    readonly cahier: number;
    readonly pages: ReadonlySet<number>;
  } | null>(null);
  const figee = selection?.cahier === cahier.id ? selection.pages : null;
  const cochees =
    cahier.etat === "clos"
      ? []
      : figee === null
        ? pagesCocheesDOffice(pages, demandes)
        : pages.filter((page) => figee.has(page.id)).map((page) => page.id);
  return {
    cochees,
    basculer: (pageId) => {
      const suivante = new Set(cochees);
      if (suivante.has(pageId)) suivante.delete(pageId);
      else suivante.add(pageId);
      setSelection({ cahier: cahier.id, pages: suivante });
    },
    vider: () => setSelection({ cahier: cahier.id, pages: new Set() }),
  };
}

/**
 * La case d'une page, posee par-dessus le coin gauche de sa carte : a cote du lien et non
 * dedans (un champ dans un lien n'est pas du HTML valide), et avant lui dans l'ordre du
 * clavier. Son etiquette l'agrandit a 36 px, pour le doigt. Le parent est en `relative`.
 */
export function CaseDePage({
  titre,
  cochee,
  cochable,
  basculer,
}: {
  /** Le titre numerote de la page : `02 La vache curieuse`. */
  titre: string;
  cochee: boolean;
  cochable: boolean;
  basculer: () => void;
}) {
  return (
    <label
      className={cn(
        "absolute top-1 left-1 z-10 flex size-9 items-center justify-center rounded-md",
        cochable ? "cursor-pointer" : "cursor-not-allowed",
      )}
    >
      <input
        aria-label={`Cocher ${titre}`}
        checked={cochee}
        className="size-4 accent-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
        disabled={!cochable}
        onChange={basculer}
        type="checkbox"
      />
    </label>
  );
}

/** Le nombre de versions par page d'une relance : de 1 a 10, 3 par defaut. */
const PAR_PAGE = Array.from({ length: 10 }, (_, rang) => rang + 1);
const PAR_PAGE_DEFAUT = 3;

/**
 * Le bouton en contour, pour les deux boutons dont la barre garde la main sur le focus :
 * `Button` du template ne transmet pas de `ref`.
 */
const BOUTON_CONTOUR = cn(LIEN_BOUTON, "disabled:pointer-events-none disabled:opacity-50");

type EtatCloture = "fermee" | "ouverte" | "annulee";

/**
 * La barre d'actions : « Relancer [3] versions » des pages cochees, au prompt de reference
 * et avec des graines jamais tirees (les variantes arrivent au ticket 4), « Monter le PDF » et
 * « Clore le cahier ». Le message d'une ecriture s'affiche dessous, y compris celui d'une
 * demande annulee dans la table : la barre reste quand la table disparait.
 */
export function ActionsDuCahier({
  cahier,
  cochees,
  demandes,
  retour,
  annoncer,
  viderLaSelection,
}: {
  cahier: CahierResume;
  /** Les pages cochees, dans l'ordre de la grille. */
  cochees: readonly number[];
  demandes: readonly Demande[];
  retour: Retour | null;
  annoncer: (retour: Retour | null) => void;
  viderLaSelection: () => void;
}) {
  const nouvelle = useNouvelleDemande();
  const [parPage, setParPage] = useState(PAR_PAGE_DEFAUT);
  const [cloture, setCloture] = useState<EtatCloture>("fermee");
  const boutonClore = useRef<HTMLButtonElement>(null);
  // Un verrou synchrone : un double clic arrive avant que `isPending` ne soit rendu, et
  // creerait deux demandes identiques.
  const envoi = useRef(false);

  const clos = cahier.etat === "clos";
  // Le PC n'a pas le graphe d'un cahier rendu par un workflow : le backend refuse de le rendre.
  const workflow = cahier.modele === "workflow";
  const montageDemande = demandes.some(
    (demande) => demande.type === "monter" && demandeActive(demande),
  );

  // Fermee sans clore (Annuler, Echap) : le focus revient au bouton qui l'a ouverte, une fois
  // la boite sortie du document (tant qu'elle est modale, le reste de la page est inerte).
  useEffect(() => {
    if (cloture === "annulee") boutonClore.current?.focus();
  }, [cloture]);

  const demander = (demande: NouvelleDemande, apres?: () => void): void => {
    if (envoi.current) return;
    envoi.current = true;
    annoncer(null);
    nouvelle.mutate(demande, {
      onSuccess: (reponse) => {
        apres?.();
        annoncer({ ton: "ok", texte: `Demande enregistrée : ${resumeDemande(reponse.demande)}.` });
      },
      onError: (error) => annoncer({ ton: "echec", texte: messageDEchec(error) }),
      onSettled: () => {
        envoi.current = false;
      },
    });
  };

  const relancer = (): void =>
    demander(
      { type: "rendre", cahier: cahier.id, pages: [...cochees], versions_par_page: parPage },
      viderLaSelection,
    );

  const nomRelancer = `Relancer ${compter(parPage, "version", "versions")} de ${compter(
    cochees.length,
    "page cochée",
    "pages cochées",
  )}`;

  return (
    <section
      aria-label="Actions sur le cahier"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card px-5 py-3.5 text-card-foreground"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5">
        <strong className="text-sm font-semibold">
          {compter(cochees.length, "page cochée", "pages cochées")}
        </strong>
        <span className="flex flex-wrap items-center gap-2">
          {/* « Relancer [3] versions » : le nombre est un champ au milieu de la phrase. Le nom du
              bouton la dit en entier, en commencant par son texte visible (WCAG 2.5.3). */}
          <Button
            aria-disabled={nouvelle.isPending || undefined}
            aria-label={nomRelancer}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            disabled={clos || workflow || cochees.length === 0}
            onClick={relancer}
          >
            Relancer
          </Button>
          <select
            aria-label="Versions par page"
            className="h-10 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            disabled={clos || workflow}
            onChange={(event) => setParPage(Number(event.target.value))}
            value={parPage}
          >
            {PAR_PAGE.map((nombre) => (
              <option key={nombre} value={nombre}>
                {nombre}
              </option>
            ))}
          </select>
          <span className="text-sm">{parPage > 1 ? "versions" : "version"}</span>
          <span className="text-xs text-muted-foreground">
            {workflow
              ? "un cahier rendu par un workflow ComfyUI ne se relance pas d'ici : le PC n'a pas son graphe"
              : "prompt de référence, graines jamais tirées"}
          </span>
        </span>
        <span className="flex flex-wrap gap-2 sm:ml-auto">
          <Button
            aria-disabled={nouvelle.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            disabled={clos || montageDemande}
            onClick={() => demander({ type: "monter", cahier: cahier.id })}
            variant="outline"
          >
            Monter le PDF
          </Button>
          <button
            className={BOUTON_CONTOUR}
            disabled={clos}
            onClick={() => setCloture("ouverte")}
            ref={boutonClore}
            type="button"
          >
            Clore le cahier
          </button>
        </span>
      </div>
      {clos ? (
        <p className="text-sm text-muted-foreground">
          Ce cahier est clos : ses pages ne se relancent plus et son PDF ne se monte plus d'ici.
        </p>
      ) : null}
      {montageDemande && !clos ? (
        <p className="text-xs text-muted-foreground">
          Un montage du PDF est déjà demandé : il part avec l'agent du PC.
        </p>
      ) : null}
      <MessageDeRetour retour={retour} />
      {cloture === "ouverte" ? (
        <DialogueCloture
          apresCloture={(bilan) => {
            setCloture("fermee");
            annoncer({ ton: "ok", texte: bilanDeCloture(bilan) });
          }}
          cahier={cahier}
          enAttente={demandes.filter((demande) => demande.etat === "en_attente").length}
          fermer={() => setCloture("annulee")}
        />
      ) : null}
    </section>
  );
}

/**
 * La confirmation de la cloture. Un `<dialog>` natif ouvert par `showModal()` : le reste de
 * la page devient inerte, le focus ne peut pas en sortir, Echap la ferme. jsdom n'a pas
 * `showModal` : la boite s'y ouvre par son attribut, et Echap passe par l'ecouteur du
 * clavier. Le focus part sur « Annuler », jamais sur le bouton qui efface.
 */
function DialogueCloture({
  cahier,
  enAttente,
  fermer,
  apresCloture,
}: {
  cahier: CahierResume;
  /** Les demandes en attente sur le cahier : la cloture les annule. */
  enAttente: number;
  fermer: () => void;
  apresCloture: (bilan: BilanCloture) => void;
}) {
  const boite = useRef<HTMLDialogElement>(null);
  const annuler = useRef<HTMLButtonElement>(null);
  const apercu = useApercuCloture(String(cahier.id));
  const clore = useClore(String(cahier.id));

  useEffect(() => {
    const dialogue = boite.current;
    if (dialogue === null) return;
    if (typeof dialogue.showModal === "function") {
      if (!dialogue.open) dialogue.showModal();
    } else {
      dialogue.setAttribute("open", "");
    }
    annuler.current?.focus();
    return () => {
      if (typeof dialogue.close === "function" && dialogue.open) dialogue.close();
    };
  }, []);

  const refus = apercu.data?.demande_en_cours === true;
  const images = apercu.data?.images_effacees ?? 0;
  // Meme verrou que la barre : un double clic ne clot qu'une fois.
  const envoi = useRef(false);
  const confirmer = (): void => {
    if (envoi.current) return;
    envoi.current = true;
    clore.mutate(undefined, {
      onSuccess: apresCloture,
      onSettled: () => {
        envoi.current = false;
      },
    });
  };

  return (
    <dialog
      aria-describedby="coloriage-cloture-apercu"
      aria-labelledby="coloriage-cloture-titre"
      className="m-auto max-h-[calc(100dvh-2rem)] w-[min(34rem,calc(100%-2rem))] overflow-auto rounded-lg border border-border bg-card p-0 text-card-foreground shadow-lg backdrop:bg-background/80"
      onCancel={(event) => {
        event.preventDefault();
        fermer();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          fermer();
        }
      }}
      ref={boite}
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-lg font-semibold" id="coloriage-cloture-titre">
          Clore « {cahier.titre} » ?
        </h2>
        <div className="flex flex-col gap-3 text-sm" id="coloriage-cloture-apercu">
          {apercu.isPending ? (
            <p className="text-muted-foreground" role="status">
              Lecture de ce que la clôture effacerait...
            </p>
          ) : null}
          {apercu.isError ? (
            <p className="text-destructive" role="alert">
              L'aperçu n'a pas pu être lu : {messageDEchec(apercu.error)}
            </p>
          ) : null}
          {apercu.data ? <ApercuCloture apercu={apercu.data} enAttente={enAttente} /> : null}
        </div>
        {clore.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {messageDEchec(clore.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            aria-disabled={clore.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            disabled={apercu.data === undefined || refus}
            onClick={confirmer}
          >
            {images > 0
              ? `Clore et effacer ${compter(images, "image", "images")}`
              : "Clore le cahier"}
          </Button>
          <button className={BOUTON_CONTOUR} onClick={fermer} ref={annuler} type="button">
            Annuler
          </button>
        </div>
      </div>
    </dialog>
  );
}

function ApercuCloture({ apercu, enAttente }: { apercu: ClotureApercu; enAttente: number }) {
  const preChoix = preChoixEnClair(apercu.pages_au_pre_choix);
  return (
    <>
      {apercu.demande_en_cours ? (
        <p className={ALERTE_ROUGE}>
          <strong className="font-semibold">Une demande tourne sur ce cahier.</strong> Attends
          qu'elle finisse, ou annule-la, avant de le clore.
        </p>
      ) : null}
      <p>
        Chaque page garde l'image de sa version choisie, ou à défaut du pré-choix du PC.{" "}
        {effacementEnClair(apercu)}
      </p>
      {preChoix === null ? null : <p className={ALERTE_AMBRE}>{preChoix}</p>}
      {enAttente > 0 ? (
        <p>
          {enAttente > 1
            ? `Les ${enAttente} demandes en attente sur ce cahier sont annulées.`
            : "La demande en attente sur ce cahier est annulée."}
        </p>
      ) : null}
    </>
  );
}
